<template>
  <div id="app">
    <AppNavbar @show-login="openLoginModal" />
    <!-- <LanguageBanner /> -->
    <router-view />
    <!--
      The marketing pitch does not belong on an account page.
      AppContact is a "get in touch, here are our offices" section aimed at
      someone deciding whether to travel with us. Rendering it under My Tickets
      meant a signed-in passenger's own page was mostly somebody else's sales
      copy — and it pushed the actual content into a thin strip at the top.
      The footer stays: it carries the legal links and app downloads, which are
      site furniture rather than a pitch.
    -->
    <AppContact v-if="!$route.meta?.hideMarketing" />
    <AppFooter />
    <LoginModal
      :visible="showLoginModal"
      @close="closeLoginModal"
    />
  </div>
</template>

<script>
import AppNavbar from "./components/AppNavbar.vue";
import AppContact from "./components/AppContact.vue";
import AppFooter from "./components/AppFooter.vue";
import { watch } from "vue";
import { useI18n } from "vue-i18n";
import LoginModal from './components/LoginModal.vue';
// import LanguageBanner from "@/components/LanguageBanner.vue";

export default {
  name: "App",
  components: {
    AppNavbar,
    AppContact,
    AppFooter,
    LoginModal,
   // LanguageBanner,
  },
  data() {
    return {
      showLoginModal: false,
      // ── Global hold-timer / tab-title blinker ─────────────────────────
      // Server's `expires_at` (ISO timestamp) is the absolute source of
      // truth — we compute remaining each tick rather than counting down
      // a local counter, so the timer stays correct across SPA route
      // changes, tab-switch throttling, sleep, etc. When the tab is in
      // the background AND a hold is active, the document title blinks a
      // countdown so the user notices on the browser tab strip.
      holdTickInterval: null,
      blinkPhase: false,
      baseTitle: "",
    };
  },
  computed: {
    holdExpiresAt() {
      return this.$store.state.bookingDetails?.expires_at || null;
    },
    hasActiveHold() {
      const bd = this.$store.state.bookingDetails;
      return !!(bd && bd.hold_token && bd.expires_at);
    },
    holdRemainingMs() {
      if (!this.holdExpiresAt) return 0;
      const t = Date.parse(this.holdExpiresAt);
      if (!Number.isFinite(t)) return 0;
      return Math.max(0, t - Date.now());
    },
  },
  watch: {
    hasActiveHold: {
      immediate: true,
      handler(active) {
        if (active) this.startHoldTimer();
        else this.stopHoldTimer();
      },
    },
  },
  mounted() {
    this.baseTitle = document.title;
    document.addEventListener("visibilitychange", this.onHoldVisibilityChange);
  },
  beforeUnmount() {
    document.removeEventListener("visibilitychange", this.onHoldVisibilityChange);
    this.stopHoldTimer();
  },
  methods: {
    openLoginModal() {
      this.showLoginModal = true;
    },
    closeLoginModal() {
      this.showLoginModal = false;
    },
    startHoldTimer() {
      if (this.holdTickInterval) return;
      // 1s cadence is plenty — browsers throttle background timers, but the
      // tick recomputes remaining from `expires_at` each time so a missed
      // beat just shows the next correct second.
      this.holdTickInterval = setInterval(this.holdTick, 1000);
      this.holdTick();
    },
    stopHoldTimer() {
      if (this.holdTickInterval) {
        clearInterval(this.holdTickInterval);
        this.holdTickInterval = null;
      }
      this.blinkPhase = false;
      this.restoreTitle();
    },
    holdTick() {
      const ms = this.holdRemainingMs;
      if (ms <= 0) {
        // Hold has expired — stop everything, the sweeper will release.
        this.stopHoldTimer();
        return;
      }
      if (document.hidden) {
        // User is on another tab — blink the tab title with the countdown.
        this.blinkPhase = !this.blinkPhase;
        const secs = Math.floor(ms / 1000);
        const mm = String(Math.floor(secs / 60)).padStart(2, "0");
        const ss = String(secs % 60).padStart(2, "0");
        document.title = this.blinkPhase
          ? `⏰ ${mm}:${ss} — Seats holding`
          : (this.baseTitle || document.title);
      } else if (this.baseTitle && document.title !== this.baseTitle && this.blinkPhase) {
        // We're visible again but a half-blink left a stale title behind.
        document.title = this.baseTitle;
        this.blinkPhase = false;
      }
    },
    onHoldVisibilityChange() {
      if (!document.hidden) {
        this.blinkPhase = false;
        this.restoreTitle();
      } else {
        // Re-cache the title each time we go hidden — routes/components may
        // have updated `document.title` while we were visible.
        this.baseTitle = document.title;
        this.holdTick();   // first blink frame is immediate, not 1s late
      }
    },
    restoreTitle() {
      if (typeof document !== "undefined" && this.baseTitle) {
        document.title = this.baseTitle;
      }
    },
  },
  setup() {
    const { locale } = useI18n();
    watch(
      locale,
      (newLang) => {
        document.documentElement.lang = newLang;
      },
      { immediate: true }
    );
  },
};
</script>

<style>
#app {
  /* margin-top: 0;  */
  margin-top: -20px; /* was -20px, remove this hack */
  padding: 0 !important;
  font-family: "Montserrat", sans-serif;
  color: #707070;
  background-color: #fff;
  overflow-x: hidden !important;
  border: 0 !important;
  min-height: 100vh;
  /* REMOVE FLEX */
}

.router-view {
  flex: 1;
  padding-bottom: 60px;
  padding-top: 80px !important;
}
</style>
